import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const SWIPE_MIN_X = 70;
const SWIPE_MAX_Y = 50;

export function AnchorTabs({ sections }) {
  const [active, setActive] = useState(sections[0]?.id);
  const [top, setTop] = useState(64);
  const [empty, setEmpty] = useState({});
  const stripRef = useRef(null);
  const touch = useRef(null);
  const suppressUntil = useRef(0);
  const visible = useMemo(() => sections.filter((s) => !empty[s.id]), [sections, empty]);

  useEffect(() => {
    const header = document.querySelector("header");
    if (header) setTop(Math.round(header.getBoundingClientRect().height));
  }, []);

  useEffect(() => {
    // Panels that render nothing (no asks, no data rooms) collapse to 0px: hide their tab.
    const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
    const ro = new ResizeObserver((entries) => {
      setEmpty((prev) => {
        const next = { ...prev };
        entries.forEach((e) => { next[e.target.id] = e.contentRect.height < 2; });
        return next;
      });
    });
    els.forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, [sections]);

  useEffect(() => {
    const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver((entries) => {
      if (Date.now() < suppressUntil.current) return; // ignore while a click-driven smooth scroll is in flight
      const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (hit[0]) setActive(hit[0].target.id);
    }, { rootMargin: "-140px 0px -55% 0px", threshold: 0 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [sections]);

  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (id && sections.some((s) => s.id === id)) setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: "start" }), 150);
  }, [sections]);

  // Keep the active pill in view on narrow screens.
  useEffect(() => {
    stripRef.current?.querySelector(`[data-tab="${active}"]`)?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [active]);

  const go = useCallback((id) => {
    suppressUntil.current = Date.now() + 800;
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActive(id);
  }, []);

  // Phone-only: a horizontal swipe on the report jumps to the previous/next section.
  useEffect(() => {
    const onStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
    const onEnd = (e) => {
      const t = touch.current; touch.current = null;
      if (!t || window.innerWidth >= 768 || !e.changedTouches?.length) return;
      const dx = e.changedTouches[0].clientX - t.x, dy = e.changedTouches[0].clientY - t.y;
      if (Math.abs(dx) < SWIPE_MIN_X || Math.abs(dy) > SWIPE_MAX_Y) return;
      const idx = visible.findIndex((s) => s.id === active);
      const next = visible[idx + (dx < 0 ? 1 : -1)];
      if (next) go(next.id);
    };
    const main = document.querySelector("main") || document.body;
    main.addEventListener("touchstart", onStart, { passive: true });
    main.addEventListener("touchend", onEnd, { passive: true });
    return () => { main.removeEventListener("touchstart", onStart); main.removeEventListener("touchend", onEnd); };
  }, [active, visible, go]);

  return (
    <nav style={{ top }} className="sticky z-20 -mx-4 mb-6 border-b border-border bg-background/90 px-4 backdrop-blur sm:-mx-6 sm:px-6" data-testid="health-anchor-tabs" aria-label="Page sections">
      <div ref={stripRef} className="flex snap-x snap-mandatory gap-1 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {visible.map((s) => (
          <button key={s.id} type="button" data-tab={s.id} onClick={() => go(s.id)} data-testid={`health-tab-${s.id}`} aria-current={active === s.id ? "true" : undefined}
            className={`shrink-0 snap-center rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${active === s.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
            {s.label}
          </button>
        ))}
      </div>
      <p className="pb-1.5 text-[10px] text-muted-foreground md:hidden" data-testid="health-swipe-hint">Swipe left or right to move between sections</p>
    </nav>
  );
}
